import React, { useRef, useState } from 'react';
import { Send } from 'lucide-react';
import { useChatSubmit } from '../../hooks/useChatSubmit';

export default function PolyChatInput({ sessionId, createSession = false }) {
  const [input, setInput] = useState('');
  const [showHint, setShowHint] = useState(false);
  const textareaRef = useRef(null);
  const { submitMessage, isSubmitting } = useChatSubmit({ sessionId, createSession });
  const hasSession = Boolean(sessionId) || createSession;
  const isDisabled = !hasSession || isSubmitting;

  const handleSend = async () => {
    if (!hasSession || !input.trim() || isSubmitting) return;
    const text = input.trim();
    setInput('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    await submitMessage(text);
  };

  const handleInput = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 128)}px`;
  };

  return (
    <div
      className="relative max-w-3xl mx-auto flex items-end gap-2 w-full"
      onMouseEnter={() => {
        if (!hasSession) setShowHint(true);
      }}
      onMouseLeave={() => setShowHint(false)}>
      {showHint && !hasSession && (
        <div className="pointer-events-none absolute -top-10 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-lg border border-white/30 bg-shell-card px-3 py-1.5 text-xs text-muted shadow-lg">
          Click <span className="text-primary font-medium">New Chat</span> to start a session
        </div>
      )}
      <textarea
        ref={textareaRef}
        value={hasSession ? input : ''}
        onChange={e => {
          if (!hasSession) return;
          setInput(e.target.value);
        }}
        onInput={handleInput}
        onKeyDown={e => {
          if (!hasSession) {
            e.preventDefault();
            return;
          }
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
          }
        }}
        disabled={!hasSession}
        readOnly={!hasSession}
        placeholder={hasSession ? 'Send a message...' : 'Click New Chat to start a session...'}
        rows={1}
        title={!hasSession ? 'Click New Chat to start a session' : undefined}
        className={`flex-1 bg-shell-card border border-shell-card-border rounded-xl px-4 py-3 text-sm text-white placeholder:text-muted-faint outline-none resize-none max-h-32 ${
          hasSession
            ? 'focus:border-primary/40'
            : 'opacity-50 cursor-not-allowed focus:border-shell-card-border'
        }`}
      />
      <button
        type="button"
        onClick={handleSend}
        disabled={isDisabled || !input.trim()}
        title={!hasSession ? 'Click New Chat to start a session' : undefined}
        className="w-11 h-11 flex-shrink-0 flex items-center justify-center bg-primary hover:bg-primary/90 text-black rounded-xl disabled:opacity-40 disabled:cursor-not-allowed transition-opacity">
        <Send className="w-4 h-4" />
      </button>
    </div>
  );
}
